import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { resolveImageUrl } from '../lib/api';
import { useLang } from '../hooks/useLang';
import { pickLocalized } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewsSection = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const [visibleCount, setVisibleCount] = useState(6);
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/news`);
        if (!response.ok) throw new Error('Failed to fetch news');
        const data = await response.json();
        setNewsItems(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const loadMore = () => setVisibleCount((prev) => prev + 6);

  if (loading) {
    return (
      <section className="py-24 px-8 lg:px-16 bg-[#f5f2ed]">
        <div className="max-w-[1920px] mx-auto">
          <h2 className="text-4xl lg:text-5xl font-light mb-16">{t('news.title')}</h2>
          <p className="text-center text-gray-500">{t('common.loading')}</p>
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <section className="py-24 px-8 lg:px-16 bg-[#f5f2ed]">
        <div className="max-w-[1920px] mx-auto">
          <h2 className="text-4xl lg:text-5xl font-light mb-16">{t('news.title')}</h2>
          <p className="text-center text-red-500">{t('common.error')}: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 px-8 lg:px-16 bg-[#f5f2ed]">
      <div className="max-w-[1920px] mx-auto">
        <h2 className="text-4xl lg:text-5xl font-light mb-16">{t('news.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {newsItems.slice(0, visibleCount).map((item) => {
            const title = pickLocalized(item, 'title', lang);
            const category = pickLocalized(item, 'category', lang);
            const Component = item.link.startsWith('/') ? Link : 'a';
            const linkProps = item.link.startsWith('/') ? { to: item.link } : { href: item.link };
            return (
              <Component key={item.id} {...linkProps} className="group block bg-white overflow-hidden transition-all duration-300 hover:shadow-lg">
                <div className="overflow-hidden bg-gray-100">
                  <img src={resolveImageUrl(item.image)} alt={title}
                    className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="text-xs font-light tracking-wider text-[#d94e47] mb-3 uppercase">{category}</div>
                  <h3 className="text-lg font-light leading-snug group-hover:text-[#d94e47] transition-colors duration-300">{title}</h3>
                </div>
              </Component>
            );
          })}
        </div>
        {visibleCount < newsItems.length && (
          <div className="mt-12 text-center">
            <button onClick={loadMore}
              className="inline-flex items-center gap-2 text-sm font-light tracking-wide hover:text-[#d94e47] transition-colors duration-300 border-b border-black hover:border-[#d94e47]">
              {t('common.viewMore')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsSection;
